import { useMsal } from '@azure/msal-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../auth/authContext.js';
import { useProfilePhoto } from '../../auth/useProfilePhoto.js';
import { AssistantPanel } from '../../features/assistant/AssistantPanel.js';
import { useAssistantStatus } from '../../features/assistant/api.js';
import { isMemberOnline, useOnlineMembersPoll } from '../../features/members/api.js';
import { FavoritesBar } from '../../features/quick-access/FavoritesBar.js';
import { QuickAccessMenus } from '../../features/quick-access/QuickAccessMenus.js';
import { Icon } from '../ui/Icon.js';
import { ThemePicker } from './ThemePicker.js';

interface TopbarProps {
  onMenuClick: () => void;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Topbar({ onMenuClick }: TopbarProps): JSX.Element {
  const { instance } = useMsal();
  const { principal } = useAuth();
  const { data: photoUrl } = useProfilePhoto();
  const { data: assistantStatus } = useAssistantStatus();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const assistantAvailable = Boolean(assistantStatus?.configured && assistantStatus?.enabled);

  const isAdmin = principal?.isAdmin === true;
  const onlineQuery = useOnlineMembersPoll(isAdmin);
  const onlineMembers = (onlineQuery.data?.data ?? []).filter((m) => isMemberOnline(m.lastSeenAt));
  const onlineCount = onlineMembers.length;

  // Briefly pulse the pill when the polled online count changes.
  const prevCountRef = useRef<number | null>(null);
  const [countFlash, setCountFlash] = useState(false);
  useEffect(() => {
    if (!onlineQuery.isSuccess) return;
    const prev = prevCountRef.current;
    prevCountRef.current = onlineCount;
    if (prev === null || prev === onlineCount) return;
    setCountFlash(true);
    const timer = setTimeout(() => setCountFlash(false), 2500);
    return () => clearTimeout(timer);
  }, [onlineQuery.isSuccess, onlineCount]);

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const name = principal?.displayName ?? account?.name ?? account?.username ?? 'Signed in';
  const subtitle = principal
    ? principal.isAdmin
      ? 'Administrator'
      : principal.type === 'member'
        ? 'Member'
        : 'API user'
    : (account?.username ?? '');

  const signOut = (): void => {
    void instance.logoutRedirect();
  };

  return (
    <header className="flex h-16 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:px-6">
      <button
        type="button"
        onClick={onMenuClick}
        className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
        aria-label="Open navigation"
      >
        <Icon name="menu" className="h-5 w-5" />
      </button>

      {isAdmin && onlineQuery.isSuccess ? (
        <Link
          to="/admin/maintenance"
          title={
            onlineCount > 0
              ? `Online now:\n${onlineMembers.map((m) => m.displayName).join('\n')}`
              : 'No members online'
          }
          className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all duration-300 ${
            countFlash
              ? 'animate-pulse border-emerald-400 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-300'
              : 'border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
          aria-label={`${onlineCount} members online — open maintenance`}
        >
          <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          <span className="tabular-nums">{onlineCount}</span>
        </Link>
      ) : null}

      <FavoritesBar />

      <div className="ml-auto flex items-center gap-3">
        <QuickAccessMenus />
        {assistantAvailable ? (
          <button
            type="button"
            onClick={() => setAssistantOpen(true)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            title="Ask PSA"
            aria-label="Ask PSA"
          >
            <Icon name="assistant" className="h-5 w-5" />
          </button>
        ) : null}
        <ThemePicker />
        <div className="hidden text-right sm:block">
          <p className="text-sm font-medium text-slate-900">{name}</p>
          {subtitle ? <p className="text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        {photoUrl ? (
          <img src={photoUrl} alt={name} className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
            {initialsOf(name)}
          </div>
        )}
        <button
          type="button"
          onClick={signOut}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          title="Sign out"
          aria-label="Sign out"
        >
          <Icon name="logout" className="h-5 w-5" />
        </button>
      </div>
      {assistantAvailable ? (
        <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      ) : null}
    </header>
  );
}
